import React, { useState, useEffect, MutableRefObject } from 'react';
import { createPortal, findDOMNode } from 'react-dom';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Dropdown } from 'react-bootstrap';
import { uniqueId } from 'lodash';

type Props = {
  alignRight?: true;
  disabled?: boolean;
  options: React.ReactElement[];
  psbRef?: MutableRefObject<PerfectScrollbar | null> | null;
  ariaLabel?: string;
};

const TableMenuToggle: React.FC<Props> = ({ alignRight, options, disabled, psbRef, ...rest }: Props) => {
  const id = uniqueId();

  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | any>(null);
  const [menuRef, setMenuRef] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ dropdownTop: 0, dropdownLeft: 0, dropdownBottom: 0 });
  const [show, setShow] = useState<boolean>(false);

  const setDropdownButtonPosition = () => {
    if (dropdownRef) {
      const dropDownContainer = dropdownRef.getBoundingClientRect();
      setPosition({ dropdownTop: dropDownContainer.top, dropdownLeft: dropDownContainer.left, dropdownBottom: dropDownContainer.bottom });
    }
  };

  const updateMenuPosition = () => {
    const menuDomNode = findDOMNode(menuRef) as HTMLElement;
    if (menuDomNode) {
      const { dropdownTop, dropdownLeft, dropdownBottom } = position;

      const scrollBarElement = (psbRef?.current as any)?._container as HTMLElement;
      const scrollBarRect = scrollBarElement.getBoundingClientRect();

      const menuHeight = menuDomNode.offsetHeight;
      const menuWidth = menuDomNode.offsetWidth;
      const menuOutsideTableBounds = dropdownBottom > scrollBarRect.bottom || dropdownTop < scrollBarRect.top + 80;
      const shouldAppendMenuTop = scrollBarRect.bottom - dropdownBottom < menuHeight;
      const { width } = dropdownRef.getBoundingClientRect();

      const menuPositionLeft = dropdownLeft - menuWidth + width;

      menuDomNode.style.transform = `translate3d(${menuPositionLeft}px, ${dropdownBottom}px, 0px)`;

      if (menuOutsideTableBounds) {
        setShow(false);
      }

      if (shouldAppendMenuTop) {
        menuDomNode.style.transform = `translate3d(${menuPositionLeft}px, ${dropdownTop - menuHeight}px, 0px)`;
      }
    }
  };

  const handleToggle = (show: boolean) => {
    setShow(show);
  };

  useEffect(() => {
    if (show) {
      updateMenuPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  useEffect(() => {
    const scrollBarElement = (psbRef?.current as any)?._container as HTMLElement;
    if (scrollBarElement) {
      scrollBarElement.addEventListener('scroll', setDropdownButtonPosition);
      return () => {
        scrollBarElement.removeEventListener('scroll', setDropdownButtonPosition);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psbRef, dropdownRef]);

  return (
    <Dropdown
      data-testid="menu-toggle"
      ref={(ref: HTMLDivElement | any) => setDropdownRef(ref)}
      show={show}
      className="menu-toggle menu-toggle-v2"
      onToggle={(show: boolean) => handleToggle(show)}
      role="menu"
    >
      <Dropdown.Toggle id={id} disabled={disabled} aria-label={rest.ariaLabel}>
        <i className="icon-ds-more-vertical" />
      </Dropdown.Toggle>

      {createPortal(
        <Dropdown.Menu
          align={alignRight ? 'end' : undefined}
          data-testid="dropdown-menu-v2"
          as="ul"
          role="menu"
          ref={ref => setMenuRef(ref as HTMLDivElement | null)}
          className="table-menu-toggle-container dropdown-menu-v2"
        >
          {options.map(option => (
            <li key={option.key} role="menuitem">
              {option}
            </li>
          ))}
        </Dropdown.Menu>,
        document.body,
      )}
    </Dropdown>
  );
};

export default TableMenuToggle;
